import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { AspectCardData } from '@/features/aspects/types';

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

interface AspectCardProps {
  data: AspectCardData;
  onDetach: (id: string) => void;
  onNavigate: (data: AspectCardData) => void;
}

export function AspectCard({ data, onDetach, onNavigate }: AspectCardProps) {
  const { t } = useI18n();

  return (
    <div
      className="group p-3 rounded-lg border border-rail-border hover:border-primary/30 hover:bg-accent/30 cursor-pointer transition-colors"
      onClick={() => onNavigate(data)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onNavigate(data); }}
    >
      <div className="flex items-start gap-3">
        <div
          className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: data.color ? `color-mix(in srgb, currentColor 10%, transparent)` : undefined }}
        >
          <svg className={`h-4 w-4 ${data.color}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d={data.icon} />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-rail-foreground truncate">{data.title}</p>
            {data.status && (
              <Badge variant={data.statusVariant as any} size="sm" className="flex-shrink-0">
                {data.status}
              </Badge>
            )}
          </div>
          {data.subtitle && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{data.subtitle}</p>
          )}
          {/* Type-specific preview */}
          <TypeSpecificPreview typeName={data.typeName} fields={data.previewFields} />
        </div>
        <div className="hidden group-hover:flex flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onDetach(data.id); }}
            className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
            aria-label={t('rail.aspects.detach')}
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function TypeSpecificPreview({ typeName: _typeName, fields }: { typeName: string; fields: Record<string, string> }) {
  const entries = Object.entries(fields);
  if (entries.length === 0) return null;

  return (
    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
      {entries.map(([key, value]) => (
        <span key={key} className="text-xs text-muted-foreground">
          <span className="font-medium">{key}:</span> {value}
        </span>
      ))}
    </div>
  );
}
